import { Link } from '@tanstack/react-router';
import { Drawer, Descriptions, Typography, Alert, Timeline, Flex, Button, Spin, Empty, Collapse, theme } from 'antd';
import { ExportOutlined, MessageOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useFlowExecutionEventsQuery, type EnrichedFlowExecution, type FlowExecutionEvent } from '@/api/logs';
import { CopyableId, buildBackstageUrl } from '@/components/conversation-overview';
import { jsonPreview } from '@/utils/jsonPreview';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { ExecutionStatusTag } from './ExecutionStatusTag';
import { formatDuration, formatWhen } from '@/utils/dateFormat';

const { Text, Paragraph } = Typography;

export interface FlowExecutionDrawerProps {
  execution: EnrichedFlowExecution | null;
  onClose: () => void;
}

/**
 * Colour of a timeline dot: red for failure events, green for completion, blue otherwise.
 * @param event - One execution step event.
 */
function eventColor(event: FlowExecutionEvent): string {
  if (/fail|error/i.test(event.type)) return 'red';
  if (/complete/i.test(event.type)) return 'green';
  return 'blue';
}

/**
 * Details of one flow execution: status, IDs, error, trigger data, timings and the step
 * timeline from flow-observer, with links to the conversation and the Backstage snapshot.
 */
export function FlowExecutionDrawer({ execution, onClose }: FlowExecutionDrawerProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const eventsQuery = useFlowExecutionEventsQuery(execution?.executionId);
  const events = eventsQuery.data ?? [];

  const backstageUrl = execution
    ? buildBackstageUrl(`/execution-snapshot?executionId=${encodeURIComponent(execution.executionId)}`)
    : null;
  const triggerJson = jsonPreview(execution?.triggerData);
  const timingsJson = jsonPreview(execution?.timings);
  const startedAt = events[0] ? dayjs(events[0].time) : null;

  return (
    <Drawer
      open={!!execution}
      onClose={onClose}
      size="min(520px, 100vw)"
      title={
        execution && (
          <ModalTitle
            icon={<NodeIndexOutlined />}
            title={
              <Flex align="center" gap={token.marginXS} wrap="wrap">
                <span>{execution.clientFlow?.name ?? t('logs.drawer.title')}</span>
                <ExecutionStatusTag status={execution.status} />
              </Flex>
            }
            subtitle={formatWhen(execution.createdAt)}
          />
        )
      }
      extra={
        backstageUrl && (
          <Button icon={<ExportOutlined />} href={backstageUrl} target="_blank" rel="noreferrer">
            {t('logs.drawer.openInBackstage')}
          </Button>
        )
      }
    >
      {execution && (
        <Flex vertical gap={token.marginLG}>
          {execution.errorMessage && (
            <Alert
              type="error"
              showIcon
              title={execution.errorMessage}
              description={execution.errorThrownInNode ? t('logs.executions.failedInNode', { node: execution.errorThrownInNode }) : undefined}
            />
          )}

          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              { key: 'time', label: t('logs.drawer.startedAt'), children: formatWhen(execution.createdAt) },
              { key: 'duration', label: t('logs.drawer.duration'), children: formatDuration(execution.durationMs, 'precise') },
              { key: 'tenant', label: t('logs.drawer.tenant'), children: execution.tenant?.client.name ?? '—' },
              {
                key: 'flow',
                label: t('logs.drawer.template'),
                children: execution.clientFlow?.masterFlow
                  ? `${execution.clientFlow.masterFlow.displayName} · v${execution.clientFlow.masterFlow.version}`
                  : '—'
              },
              { key: 'trigger', label: t('logs.drawer.triggeredBy'), children: execution.triggeredBy ?? '—' },
              { key: 'executionId', label: t('logs.drawer.executionId'), children: <CopyableId value={execution.executionId} /> },
              {
                key: 'correlationId',
                label: t('logs.drawer.correlationId'),
                children: execution.correlationId ? <CopyableId value={execution.correlationId} /> : '—'
              },
              { key: 'userId', label: t('logs.drawer.userId'), children: execution.userId ? <CopyableId value={execution.userId} /> : '—' },
              {
                key: 'thread',
                label: t('logs.drawer.conversation'),
                children: execution.threadId ? (
                  <Link to="/admin/conversation-overview/$threadId" params={{ threadId: execution.threadId }}>
                    <MessageOutlined /> {t('logs.drawer.openConversation')}
                  </Link>
                ) : (
                  '—'
                )
              }
            ]}
          />

          <div>
            <Text strong>{t('logs.drawer.steps')}</Text>
            <div style={{ marginTop: token.marginSM }}>
              {eventsQuery.isLoading ? (
                <Spin />
              ) : events.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('logs.drawer.noSteps')} />
              ) : (
                <Timeline
                  items={events.map((event) => ({
                    key: event.id,
                    color: eventColor(event),
                    content: (
                      <div>
                        <Flex gap={token.marginXS} align="baseline" wrap="wrap">
                          <Text strong>{event.displayName ?? event.type}</Text>
                          {event.label && <Text type="secondary">{event.label}</Text>}
                          {startedAt && (
                            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                              +{formatDuration(dayjs(event.time).diff(startedAt), 'precise')}
                            </Text>
                          )}
                        </Flex>
                        {event.clientExplanation && event.clientExplanation !== event.displayName && (
                          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            {event.clientExplanation}
                          </Paragraph>
                        )}
                      </div>
                    )
                  }))}
                />
              )}
            </div>
          </div>

          {(triggerJson || timingsJson) && (
            <Collapse
              size="small"
              items={[
                ...(triggerJson
                  ? [{ key: 'trigger', label: t('logs.drawer.triggerData'), children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{triggerJson}</pre> }]
                  : []),
                ...(timingsJson
                  ? [{ key: 'timings', label: t('logs.drawer.timings'), children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{timingsJson}</pre> }]
                  : [])
              ]}
            />
          )}
        </Flex>
      )}
    </Drawer>
  );
}
